import { MessageStore } from '../stores/MessageStore.svelte.js';
import { ValueService } from './ValueService.js';

/**
 * @template T
 * @typedef {import('../types/api.d.js').ApiResponse<T>} ApiResponse
 */

/**
 * @typedef {Omit<RequestInit, 'body'> & {body?:any}} ApiRequestInit
 */

/**
 * @typedef {import('../types/api.d.js').ApiUsersListItem} ApiUsersListItem
 * @typedef {import('../types/api.d.js').ApiBoolData} ApiBoolData
 * @typedef {import('../types/api.d.js').ApiLoginData} ApiLoginData
 * @typedef {import('../types/api.d.js').ApiUsersListData} ApiUsersListData
 * @typedef {import('../types/api.d.js').ApiAiAnnouncementListData} ApiAiAnnouncementListData
 * @typedef {import('../types/api.d.js').ApiAiAnnouncementData} ApiAiAnnouncementData
 */

class ApiServiceDefinition {
    /**
     * @template T
     * @param {boolean} ok
     * @param {number} statusCode
     * @param {string} message
     * @param {T} data
     * @returns {ApiResponse<T>}
     */
    static createResponse(ok, statusCode, message, data = /** @type {T} */ ({})) {
        if (statusCode !== 200) {
            MessageStore.error(`#${statusCode}: ${message}`);
        }

        return {
            ok,
            statusCode,
            message,
            data,
        };
    }
}

export class ApiService {
    /**
     * @template T
     * @param {string} endpoint
     * @param {ApiRequestInit} options
     * @returns {Promise<ApiResponse<T>>}
     */
    async request(endpoint, options = {}) {
        /** @type {{ [key: string]: string }} */
        const headers = {};

        let body = options.body;

        if (body instanceof URLSearchParams) {
            // Browser setzt application/x-www-form-urlencoded automatisch
        } else if (body instanceof FormData) {
            // Browser setzt multipart/form-data automatisch inkl. Boundary
        } else if (body !== undefined && body !== null) {
            headers['Content-Type'] = 'application/json';
            body = JSON.stringify(body);
        }

        let response;

        try {
            response = await fetch(`/api/v1${endpoint}`, {
                credentials: 'include',
                ...options,
                headers: {
                    ...headers,
                    ...options.headers,
                },
                body,
            });
        } catch {
            return ApiServiceDefinition.createResponse(false, 998, 'Keine Verbindung zum Server möglich.');
        }

        let json;

        try {
            json = await response.json();
        } catch {
            return ApiServiceDefinition.createResponse(false, 999, 'Ungültige Serverantwort!');
        }

        const statusCode = ValueService.toInt(json?.status?.code, 999);
        const message = ValueService.toStringSingleLine(json?.status?.message);

        if (statusCode !== 200 || message !== 'OK') {
            return ApiServiceDefinition.createResponse(false, statusCode, message || 'Ungültiger Status!', json);
        }

        return ApiServiceDefinition.createResponse(true, 200, 'OK', json);
    }

    /**
     * @returns {Promise<ApiResponse<Object>>}
     */
    logout() {
        return this.request('/logout');
    }

    /**
     * @param {string} user
     * @param {string} password
     * @returns {Promise<ApiResponse<ApiLoginData>>}
     */
    async login(user, password) {
        const response = await this.request('/login', {
            method: 'POST',
            body: new URLSearchParams({
                user,
                password,
            }),
        });

        const data = /** @type {ApiLoginData} */ (response.data);

        response.data = {
            userId: ValueService.toUIntOrNull(data.userId),
            userName: ValueService.toStringSingleLine(data.userName),
            userAcronym: ValueService.toStringSingleLine(data.userAcronym),
        };

        return response;
    }

    /**
     * @returns {Promise<ApiResponse<ApiAiAnnouncementListData>>}
     */
    async getAiAnnouncement() {
        const response = await this.request('/ai/announcement');

        // -
        const data = /** @type {ApiAiAnnouncementListData} */ (response.data);
        response.data = {
            page: ValueService.toUInt(data.page, 1),
            limit: ValueService.toUInt(data.limit, 99999),
            total: ValueService.toUInt(data.total, 0),

            items: Array.isArray(data.items) ? data.items.map((item) => this.validAiAnnouncementRow(item)) : [],
        };

        // -
        return response;
    }

    /**
     * @param {ApiAiAnnouncementData} item
     * @returns {ApiAiAnnouncementData}
     */
    validAiAnnouncementRow(item) {
        return {
            ai_announcement_id: ValueService.toUIntOrNull(item.ai_announcement_id),
            user_id: ValueService.toUIntOrNull(item.user_id),
            system: ValueService.toStringSingleLine(item.system),
            company: ValueService.toStringSingleLine(item.company),
            used_for: ValueService.toStringSingleLine(item.used_for),
            used_since: ValueService.toDate(item.used_since),
            jobs: ValueService.toString(item.jobs),
            data_type: ValueService.toString(item.data_type),
            is_pii: ValueService.toBool(item.is_pii),
            risk: ValueService.toStringSingleLine(item.risk),
            role: ValueService.toStringSingleLine(item.role),
            human_oversight: ValueService.toStringSingleLine(item.human_oversight),
            is_art4: ValueService.toBool(item.is_art4),
            is_art50: ValueService.toBool(item.is_art50),
            is_art14: ValueService.toBool(item.is_art14),
            is_art27: ValueService.toBool(item.is_art27),
            is_dsfa: ValueService.toBool(item.is_dsfa),
            is_avv: ValueService.toBool(item.is_avv),
            is_eu_hosting: ValueService.toBool(item.is_eu_hosting),
            todo_message: ValueService.toString(item.todo_message),
            todo_name: ValueService.toStringSingleLine(item.todo_name),
            todo_till: ValueService.toDate(item.todo_till),
            created_at: ValueService.toDateTimeLocal(item.created_at),
            updated_at: ValueService.toDateTimeLocal(item.updated_at),
            update_sequence: ValueService.toUInt(item.update_sequence),
        };
    }

    /**
     * @param {ApiAiAnnouncementData} row
     * @returns {Promise<ApiResponse<ApiAiAnnouncementData>>}
     */
    async postAiAnnouncement(row) {
        const response = await this.request('/ai/announcement', {
            method: 'POST',
            body: new URLSearchParams(Object.entries(row).map(([key, value]) => [key, ValueService.toString(value)])),
        });

        // -
        response.data = this.validAiAnnouncementRow(/** @type {ApiAiAnnouncementData} */ (response.data));

        // -
        return response;
    }

    /**
     * @param {number} id
     * @param  {{ [key: string]: string|number|boolean|null }} data
     * @returns {Promise<ApiResponse<Object>>}
     */
    async patchUser(id, data) {
        return this.request('/users/' + id, {
            method: 'PATCH',
            body: data,
        });
    }

    /**
     * @param {ApiUsersListItem} item
     * @returns {ApiUsersListItem}
     */
    validUserRow(item) {
        const firstName = ValueService.toStringSingleLine(item.firstName);
        const lastName = ValueService.toStringSingleLine(item.lastName);
        let userAcronym = ValueService.toStringSingleLine(item.userAcronym);
        if (userAcronym === '') {
            userAcronym = firstName.substring(0, 1).toLocaleUpperCase() + lastName.substring(0, 1).toLocaleUpperCase() + lastName.substring(1, 2).toLocaleLowerCase();
        }

        return {
            userId: ValueService.toUIntOrNull(item.userId),
            userName: ValueService.toStringSingleLine(item.userName),
            userAcronym: userAcronym,
            nickName: ValueService.toStringSingleLine(item.nickName),
            firstName: firstName,
            lastName: lastName,
            color: ValueService.toStringSingleLine(item.color),
            email: ValueService.toStringSingleLine(item.email),
            groups: Array.isArray(item.groups) ? item.groups.map((v) => ValueService.toStringSingleLine(v)) : [],
        };
    }

    /**
     * @returns {Promise<ApiResponse<ApiUsersListItem>>}
     */
    async whoAmI() {
        const response = await this.request('/whoami');
        response.data = this.validUserRow(/** @type {ApiUsersListItem} */ (response.data));

        return response;
    }

    /**
     * @returns {Promise<ApiResponse<ApiUsersListData>>}
     */
    async getUsers() {
        const response = await this.request('/users');

        const data = /** @type {ApiUsersListData} */ (response.data);

        response.data = {
            page: ValueService.toUInt(data.page, 1),
            limit: ValueService.toUInt(data.limit, 99999),
            total: ValueService.toUInt(data.total, 0),
            items: Array.isArray(data.items) ? data.items.map((item) => this.validUserRow(item)) : [],
        };

        return response;
    }

    /**
     * @param {string} email
     * @returns {Promise<ApiResponse<Object>>}
     */
    async callUserResetPasswordCode(email) {
        return await this.request('/user/reset-password-code', {
            method: 'POST',
            body: new URLSearchParams({
                email,
            }),
        });
    }

    /**
     * @param {string} email
     * @param {string} code
     * @returns {Promise<ApiResponse<Object>>}
     */
    async callUserValidatePasswordCode(email, code) {
        return await this.request('/user/validate-password-code', {
            method: 'POST',
            body: new URLSearchParams({
                email,
                code,
            }),
        });
    }

    /**
     * @param {string} email
     * @param {string} code
     * @param {string} password
     * @returns {Promise<ApiResponse<Object>>}
     */
    async callUserResetPasswordValue(email, code, password) {
        return await this.request('/user/reset-password-value', {
            method: 'POST',
            body: new URLSearchParams({
                email,
                code,
                password,
            }),
        });
    }

    /**
     * @param {string} email
     * @returns {Promise<ApiResponse<ApiBoolData>>}
     */
    async callUserRegisterEmailIsValid(email) {
        const response = await this.request('/user/register-email-is-valid', {
            method: 'POST',
            body: new URLSearchParams({ email }),
        });

        return this._asBoolResponse(response);
    }

    /**
     * @param {string} email
     * @returns {Promise<ApiResponse<ApiBoolData>>}
     */
    async callUserRegisterByEmail(email) {
        const response = await this.request('/user/register-by-email', {
            method: 'POST',
            body: new URLSearchParams({ email }),
        });

        return this._asBoolResponse(response);
    }

    /**
     * @param {ApiResponse<Object>} response
     * @returns {ApiResponse<ApiBoolData>}
     */
    _asBoolResponse(response) {
        const data = /** @type {ApiBoolData} */ (response.data);

        response.data = {
            value: ValueService.toBool(data.value),
        };

        return response;
    }
}
